import {
  AlertOutlined,
  ApiOutlined,
  BellOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DislikeOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LikeOutlined,
  MedicineBoxOutlined,
  MessageOutlined,
  NotificationOutlined,
  PhoneOutlined,
  SafetyOutlined,
  SnippetsOutlined,
  SolutionOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Input,
  List,
  message,
  Modal,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { useNavigate, useParams, Link } from 'react-router-dom'
import queryString from 'query-string'
import dayjs from 'dayjs'
import formatPhoneNumberSafe from '../../lib/phone'
import api from '../../api'
import { useAsync } from '../../hooks'
import useLocalStorage from '../../hooks/useLocalStorage'
import { formatDOB } from '../../lib/datetime'
import { useIsAdmin, useFeatureEnabled } from '../../redux/slices/session'
import { toPath } from '../../routes'
import { ListError } from '../Common'
import Loading from '../Loading'
import Empty from '../Common/Empty'
import { formatMedicalProvider } from '../Forms/MedicalProviderSelect'
import CommentsTables from '../Comments/CommentsTable'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import SectionCard from '../Common/SectionCard'
import FullRemovalModal from './FullRemovalModal'
import TransmissionsModal from './TransmissionsModal'
import ConnectivityNotificationModal from './ConnectivityNotificationModal'
import WelcomeLetter from './WelcomeLetter'
import LeadInfo from './LeadInfo'
import EstimatedLongevity from './EstimatedLongevity'
import DiseasesList from './DiseasesList'
import FieldItem from './FieldItem'
import Timeline from './Timeline'

const { Text } = Typography

const DEACTIVATION_OPTIONS = [
  { value: 'non_compliant', label: 'Non-Compliant' },
  { value: 'inactive_insurance', label: 'Inactive Insurance' },
  { value: 'eri_eol', label: 'ERI/EOL' },
  { value: 'explanted', label: 'Explanted' },
  { value: 'transferred_care', label: 'Transferred Care' },
  { value: 'patient_deceased', label: 'Patient Deceased' },
  { value: 'other', label: 'Other' },
]

const SUBTYPES_WITH_FREE_TEXT = ['other', 'transferred_care']

const useStyles = createUseStyles({
  page: {
    padding: '0 4px',
    maxWidth: 1200,
  },
  // Hero header card — sticky on desktop
  heroCard: {
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: '16px 24px',
    marginBottom: 16,
    position: 'sticky',
    top: 0,
    zIndex: 200,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  heroTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  heroLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flex: 1,
    minWidth: 0,
  },
  backButton: {
    flexShrink: 0,
  },
  heroInfo: {
    flex: 1,
    minWidth: 0,
  },
  heroNameRow: {
    marginBottom: 4,
  },
  heroName: {
    fontSize: 24,
    fontWeight: 700,
    color: ({ theme }) => theme.brandPrimary,
    margin: 0,
    lineHeight: 1.1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    lineHeight: '22px',
  },
  heroTag: {
    borderRadius: 12,
    fontSize: 11,
    lineHeight: '18px',
    padding: '0 8px',
    margin: 0,
  },
  metaDot: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    backgroundColor: '#d0d0d0',
    flexShrink: 0,
  },
  summaryItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: ({ theme }) => theme.textGrayColor,
    fontSize: 13,
    whiteSpace: 'nowrap',
    '& .anticon': {
      fontSize: 12,
    },
  },
  heroActions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
    alignItems: 'center',
  },
  // Key-value fields
  fieldGrid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px 32px',
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '14px 32px',
    '@media (min-width: 1200px)': {
      gridTemplateColumns: 'repeat(4, 1fr)',
    },
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: ({ theme }) => theme.textGrayColor,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  // Subsection divider
  subDivider: {
    margin: '16px 0',
    borderColor: '#f5f5f5',
  },
  subTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: ({ theme }) => theme.textGrayColor,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 12,
  },
  // Cardiac device
  deviceGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  longevityCard: {
    background: '#fafafa',
    borderRadius: 6,
    padding: '12px 16px',
    marginTop: 16,
  },
  // Notification preferences
  prefRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  prefItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  prefDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  // Status flags
  statusRow: {
    display: 'flex',
    gap: 20,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    fontWeight: 500,
  },
  // ICD Codes
  icdList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 12,
  },
  icdItem: {
    fontSize: 13,
    color: ({ theme }) => theme.textColor,
    padding: '4px 0',
    borderBottom: '1px solid #fafafa',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  icdCode: {
    fontWeight: 600,
    marginRight: 6,
  },
  // Transmissions
  trItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderRadius: 6,
    transition: 'background .15s',
    '&:hover': {
      background: '#fafafa',
    },
    '&:not(:last-child)': {
      borderBottom: '1px solid #f5f5f5',
    },
  },
  trLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  trDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: ({ theme }) => theme.blue,
    flexShrink: 0,
  },
  trDate: {
    fontSize: 14,
    fontWeight: 500,
    color: ({ theme }) => theme.textColor,
    minWidth: 140,
  },
  trFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #f5f5f5',
  },
  // Medications
  medList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  medItem: {
    padding: '10px 0',
    '&:not(:last-child)': {
      borderBottom: '1px solid #f5f5f5',
    },
  },
  medHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  medName: {
    fontSize: 14,
    fontWeight: 500,
    color: ({ theme }) => theme.textColor,
    flex: 1,
  },
  medDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px 16px',
  },
  medDetail: {
    fontSize: 13,
    color: ({ theme }) => theme.textGrayColor,
  },
  // Alert recipients
  recipientList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  recipientItem: {
    fontSize: 14,
    color: ({ theme }) => theme.textColor,
  },
  // Modal buttons
  dangerZoneButton: {
    marginLeft: 'auto',
  },
  modalButtons: {
    textAlign: 'right',
  },
})

const DEFAULT_COLLAPSED = [
  'welcomeLetter',
  'insuranceMedicalProvider',
  'cardiacDevice',
  'diseasesHistory',
  'medications',
  'notificationPreferences',
  'transmissions',
  'comments',
]

export default function Details() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isAdmin = useIsAdmin()
  const theme = useTheme()
  const [practice, setPractice] = useState({ id: '' })
  const [medicalProviders, setMedicalProviders] = useState(null)
  const [officeLocation, setOfficeLocation] = useState(null)
  const back = sessionStorage.getItem('patientReturnTo') || '/patients'

  const [userSettings, setUserSettings] = useLocalStorage('userSettings', {})
  const collapsePrefs = userSettings.PATIENT_PROFILE_COLLAPSE_PREFERENCES || DEFAULT_COLLAPSED
  const collapsePrefRef = useRef(collapsePrefs)
  collapsePrefRef.current = collapsePrefs

  const isSectionCollapsed = key => collapsePrefs.includes(key)

  const handleCollapseChange = useCallback(
    sectionKey => isCollapsed => {
      const { current } = collapsePrefRef
      const next = isCollapsed
        ? [...current.filter(k => k !== sectionKey), sectionKey]
        : current.filter(k => k !== sectionKey)
      setUserSettings(prev => ({
        ...prev,
        PATIENT_PROFILE_COLLAPSE_PREFERENCES: next,
      }))
    },
    [setUserSettings],
  )

  const goToEdit = useCallback(
    () => navigate(toPath.patients.patient.edit({ id })),
    [navigate, id],
  )
  const goToAlert = () =>
    navigate(
      toPath.alerts.new().concat('?', queryString.stringify({ patientId: id, from: 'patient' })),
    )
  const goToDevices = () => navigate(toPath.patients.patient.devices({ id }))

  const [removalOptionsVisible, setRemovalOptionsVisible] = useState(false)
  const [deactivationSubType, setDeactivationSubType] = useState(null)
  const [deactivationReason, setDeactivationReason] = useState('')
  const [commentsVersion, setCommentsVersion] = useState(0)
  const hideRemovalOptions = useCallback(() => {
    setRemovalOptionsVisible(false)
    setDeactivationSubType(null)
    setDeactivationReason('')
  }, [])
  const showRemovalOptions = useCallback(() => setRemovalOptionsVisible(true), [])

  const [remindersOptionsVisible, setRemindersOptionsVisible] = useState(false)
  const hideRemindersOptions = useCallback(() => setRemindersOptionsVisible(false), [])
  const showRemindersOptions = useCallback(() => setRemindersOptionsVisible(true), [])

  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false)
  const hidePreferencesModal = useCallback(() => setPreferencesModalVisible(false), [])
  const showPreferencesModal = useCallback(() => setPreferencesModalVisible(true), [])

  const [showTransmissionsModal, setShowTransmissionsModal] = useState(false)
  const showTransmissions = useCallback(() => setShowTransmissionsModal(true), [])

  const [notificationModalVisible, setNotificationModalVisible] = useState(false)
  const hideNotificationModal = useCallback(() => setNotificationModalVisible(false), [])
  const showNotificationModal = useCallback(() => setNotificationModalVisible(true), [])
  const timelineRef = useRef(null)
  const handleNotificationSuccess = useCallback(
    () => timelineRef.current?.refreshNotifications(),
    [],
  )

  const [fullRemovalVisible, setFullRemovalVisible] = useState(false)
  const [remindTransmissions, setRemindTransmissions] = useState(false)
  const [notifyDataReception, setNotifyDataReception] = useState(false)
  const [transmissionsCount, setTransmissionsCount] = useState(0)
  const [transmissionsList, setTransmissionsList] = useState(0)
  const hideFullRemoval = useCallback(() => setFullRemovalVisible(false), [])
  const showFullRemoval = useCallback(() => setFullRemovalVisible(true), [])

  const getPatient = useCallback(async () => {
    const patient = await api.patients.get(id)
    setRemindTransmissions(patient.preferences.remindTransmissions)
    setNotifyDataReception(patient.preferences.notifyDataReception)
    return patient
  }, [id])

  const {
    pending,
    value: patient,
    error,
    clearError,
    execute,
  } = useAsync(getPatient)

  useEffect(() => {
    ;(async () => {
      if (patient?.practice?.id) {
        setPractice(await api.practices.getPractice(patient.practice.id))
        if (!medicalProviders) {
          setMedicalProviders(
            await api.practices.getMedicalProviders(patient.practice.id),
          )
        }
      }
    })()
  }, [medicalProviders, patient])

  const medicalProvider = useMemo(() => {
    const provider = medicalProviders?.find(
      p => p.id === patient?.medicalProvider?.id,
    )
    return provider ? formatMedicalProvider(provider) : ''
  }, [medicalProviders, patient])

  // eslint-disable-next-line consistent-return
  useMemo(() => {
    if (!patient?.officeLocation) return null
    setOfficeLocation(patient.officeLocation)
  }, [patient])

  const deactivate = useCallback(async () => {
    try {
      await api.patients.destroy(id)
    } catch (e) {
      message.error(`Could not deactivate patient: ${e.message}`)
      return
    }

    const title =
      DEACTIVATION_OPTIONS.find(o => o.value === deactivationSubType)?.label
      || deactivationSubType
    const body =
      SUBTYPES_WITH_FREE_TEXT.includes(deactivationSubType)
        ? deactivationReason.trim() || 'No reason provided'
        : undefined

    try {
      await api.notifications.create({
        patientId: id,
        notificationType: 'deactivation',
        subType: deactivationSubType,
        title,
        body,
      })
      message.success('Patient successfully deactivated')
    } catch (e) {
      message.warning(
        'Patient was deactivated but the notification could not be created',
      )
    }

    hideRemovalOptions()
    execute()
    timelineRef.current?.refreshNotifications()
    setCommentsVersion(v => v + 1)
  }, [deactivationReason, deactivationSubType, execute, hideRemovalOptions, id])

  const reactivate = useCallback(async () => {
    try {
      await api.patients.update(id, { active: true })
      message.success('Patient successfully reactivated')
      execute()
    } catch (e) {
      message.error(`Could not reactivate patient: ${e.message}`)
    }
  }, [execute, id])

  const approve = useCallback(async () => {
    try {
      await api.patients.update(id, { approved: true })
      message.success('Patient successfully approved')
      execute()
    } catch (e) {
      message.error(`Could not approve patient: ${e.message}`)
    }
  }, [execute, id])

  const unapprove = useCallback(async () => {
    try {
      await api.patients.update(id, { approved: false })
      message.success('Patient successfully unapproved')
      execute()
    } catch (e) {
      message.error(`Could not unapprove patient: ${e.message}`)
    }
  }, [execute, id])

  const remindCall = useCallback(async () => {
    try {
      await api.patients.remind([id], 'voice')
      hideRemindersOptions()
      message.success('Reminder call sent')
      execute()
    } catch (e) {
      message.error(`Could not remind patient: ${e.message}`)
    }
  }, [execute, hideRemindersOptions, id])

  const remindSms = useCallback(async () => {
    try {
      await api.patients.remind([id], 'sms')
      hideRemindersOptions()
      message.success('Reminder SMS sent')
      execute()
    } catch (e) {
      message.error(`Could not remind patient: ${e.message}`)
    }
  }, [execute, hideRemindersOptions, id])

  const cancelPreferences = useCallback(() => {
    setRemindTransmissions(patient.preferences.remindTransmissions)
    setNotifyDataReception(patient.preferences.notifyDataReception)
    setPreferencesModalVisible(false)
  }, [patient])

  const savePreferences = useCallback(async () => {
    try {
      await api.patients.savePreferences(id, {
        remindTransmissions,
        notifyDataReception,
      })
      await getPatient()
      hidePreferencesModal()
      message.success('Preferences saved successfully')
    } catch (e) {
      message.error(`Could not save preferences: ${e.message}`)
    }
  }, [hidePreferencesModal, id, remindTransmissions, notifyDataReception, getPatient])

  const goToOfficeCheck = () => {
    navigate(
      toPath.practices.officeChecks
        .new({ practiceId: practice?.id || patient?.practiceId })
        .concat('?', queryString.stringify({ patientId: id })),
    )
  }

  const handleDataReceptionChange = e => setNotifyDataReception(e.target.checked)
  const handleReminderChange = e => setRemindTransmissions(e.target.checked)

  const { pending: deactivating, execute: handleDeactivate } = useAsync(deactivate, false)
  const { pending: makingReminderCall, execute: makeReminderCall } = useAsync(remindCall, false)
  const { pending: sendingReminderSms, execute: sendReminderSms } = useAsync(remindSms, false)

  const inOfficeChecksEnabled = useFeatureEnabled('in_office_checks')
  const classes = useStyles({ theme })

  const getTransmissions = useCallback(
    async () => api.patients.getTransmissions(id, 5),
    [id],
  )

  const { value: transmissionsResponse } = useAsync(getTransmissions)

  useEffect(() => {
    setTransmissionsList(transmissionsResponse?.body || [])
    setTransmissionsCount(transmissionsResponse?.headers?.get('X-Total') || 'N/A')
  }, [transmissionsResponse])

  if (pending) return <Loading large />

  if (error) {
    return <ListError description={error.message} onClose={clearError} />
  }

  const { currentDevice: device } = patient

  const showWelcomeLetterButton =
    Number.isInteger(patient?.letterStatus) && patient?.welcomeLetterTemplate

  const patientAge = patient.birthDate
    ? dayjs().diff(dayjs(patient.birthDate), 'year')
    : null

  const icdCodes = patient.deviceIcdCode
    ? patient.icdCodes.concat([patient.deviceIcdCode])
    : patient.icdCodes

  return (
    <div className={classes.page}>
      {/* ===== HERO HEADER CARD ===== */}
      <div className={classes.heroCard}>
        <div className={classes.heroTop}>
          <div className={classes.heroLeft}>
            <Button
              className={classes.backButton}
              icon={<LeftOutlined />}
              onClick={() => navigate(back)}
            />
            <div className={classes.heroInfo}>
              <div className={classes.heroNameRow}>
                <h2 className={classes.heroName}>
                  {patient.firstName} {patient.lastName}
                </h2>
              </div>
              <div className={classes.heroMeta}>
                {patient.approved ? (
                  <Tag className={classes.heroTag} color="success">approved</Tag>
                ) : (
                  <Tag className={classes.heroTag} color="warning">pending</Tag>
                )}
                {!patient.active && (
                  <Tag className={classes.heroTag}>deactivated</Tag>
                )}
                {inOfficeChecksEnabled && (
                  <div className={classes.statusRow} style={{ marginTop: 0, marginBottom: 0 }}>
                    <div className={classes.statusItem}>
                      <CircleCheckIcon checked style={{ color: theme.brandSuccess }} />
                      <span>In-Office Checks</span>
                    </div>
                    {patient.remoteMonitoring && (
                      <div className={classes.statusItem}>
                        <CircleCheckIcon checked style={{ color: theme.brandSuccess }} />
                        <span>Remote Monitoring</span>
                      </div>
                    )}
                  </div>
                )}

                {isAdmin && patient.practice && (
                  <>
                    <span className={classes.metaDot} />
                    <span className={classes.summaryItem}>{patient.practice.name}</span>
                  </>
                )}
                </div><div className={classes.heroMeta}>
                {patient.birthDate && (
                  <span className={classes.summaryItem}>
                    {formatDOB(patient.birthDate)}{patientAge !== null && ` (${patientAge}y)`}
                  </span>
                )}
                {patient.mobilePhone && (
                  <>
                    <span className={classes.metaDot} />
                    <span className={classes.summaryItem}>
                      <PhoneOutlined />
                      {formatPhoneNumberSafe(patient.mobilePhone)}
                    </span>
                  </>
                )}
                {device?.modelType && (
                  <>
                    <span className={classes.metaDot} />
                    <span className={classes.summaryItem}>
                      <HeartOutlined />
                      {device.modelType}
                    </span>
                  </>
                )}
                {patient.practiceMrn && (
                  <>
                    <span className={classes.metaDot} />
                    <span className={classes.summaryItem}>
                      MRN: {patient.practiceMrn}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={classes.heroActions}>
            <Button icon={<EditOutlined />} onClick={goToEdit}>
              Edit
            </Button>
            <Dropdown
              menu={{
                items: [
                  { key: '1', icon: <EditOutlined />, label: 'Edit', onClick: goToEdit },
                  { key: '2', icon: <HeartOutlined />, label: "Manage patient's devices", onClick: goToDevices },
                  ...(isAdmin && !patient.approved ? [{ key: '3', icon: <LikeOutlined />, label: 'Approve', onClick: approve }] : []),
                  ...(isAdmin && patient.approved ? [{ key: '4', icon: <DislikeOutlined />, label: 'Unapprove', onClick: unapprove }] : []),
                  ...(isAdmin && !patient.active ? [{ key: '5', icon: <ApiOutlined />, label: 'Reactivate', onClick: reactivate }] : []),
                  ...(isAdmin ? [{ key: '6', icon: <DeleteOutlined />, label: 'Removal options', onClick: showRemovalOptions }] : []),
                  ...(isAdmin ? [{ key: '7', icon: <PhoneOutlined />, label: 'Send reminder', onClick: showRemindersOptions, disabled: !remindTransmissions }] : []),
                  ...(isAdmin && patient.active ? [{ key: '8', icon: <AlertOutlined />, label: 'Create Alert', onClick: goToAlert }] : []),
                  ...(isAdmin && patient.active ? [{ key: '9', icon: <NotificationOutlined />, label: 'Create Connectivity Notification', onClick: showNotificationModal }] : []),
                  ...(isAdmin && patient.active ? [{ key: '10', icon: <SnippetsOutlined />, label: 'Create Office Check', onClick: goToOfficeCheck }] : []),
                ],
              }}
            >
              <Button>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* ===== WELCOME LETTER ===== */}
      <WelcomeLetter
        patient={{
          ...patient,
          welcome_letter_template: 'manual_active',
        }}
        practice={practice}
        showWelcomeLetterButton={showWelcomeLetterButton}
        collapsible
        defaultCollapsed={isSectionCollapsed('welcomeLetter')}
        onCollapseChange={handleCollapseChange('welcomeLetter')}
      />

      {/* ===== PERSONAL INFORMATION & ADDRESS ===== */}
      <SectionCard title="Personal Information" icon={<UserOutlined />} collapsible defaultCollapsed={isSectionCollapsed('personalInformation')} onCollapseChange={handleCollapseChange('personalInformation')}>
        <div className={classes.fieldGrid}>
          <FieldItem label="First Name" value={patient.firstName} classes={classes} />
          <FieldItem label="Last Name" value={patient.lastName} classes={classes} />
          <FieldItem label="Date of Birth" value={formatDOB(patient.birthDate)} classes={classes} />
          <FieldItem
            label="Email"
            value={patient.email}
                       link={patient.email ? `mailto:${patient.email}` : null}
          />
          <FieldItem
            label="Mobile Phone"
            value={formatPhoneNumberSafe(patient.mobilePhone)}
                     />
          <FieldItem
            label="Home Phone"
            value={formatPhoneNumberSafe(patient.homePhone)}
                     />
          <FieldItem label="MRN" value={patient.practiceMrn} classes={classes} />
        </div>

        <Divider className={classes.subDivider} />
        <div className={classes.subTitle}>Address</div>
        <div className={classes.fieldGrid}>
          <FieldItem label="Street" value={patient.addressStreet} classes={classes} />
          <FieldItem label="Apt/Suite" value={patient.addressApt} classes={classes} />
          <FieldItem label="City" value={patient.addressCity} classes={classes} />
          <FieldItem label="State" value={patient.addressState} classes={classes} />
          <FieldItem label="Country" value={patient.addressCountry} classes={classes} />
          <FieldItem label="Zip Code" value={patient.addressZipCode} classes={classes} />
        </div>
      </SectionCard>

      {/* ===== INSURANCE & MEDICAL PROVIDER ===== */}
      <SectionCard title="Insurance & Medical Provider" icon={<SafetyOutlined />} collapsible defaultCollapsed={isSectionCollapsed('insuranceMedicalProvider')} onCollapseChange={handleCollapseChange('insuranceMedicalProvider')}>
        <div className={classes.subTitle}>Insurance</div>
        <div className={classes.fieldGrid2}>
          <FieldItem
            label="Insurance Provider"
            value={patient.insuranceProvider?.name}
                     />
          <FieldItem
            label="Insurance Policy #"
            value={patient.insurancePolicyNumber}
                     />
        </div>

        <Divider className={classes.subDivider} />
        <div className={classes.subTitle}>Medical Provider</div>
        <Row gutter={32}>
          <Col span={12}>
            <div className={classes.fieldGrid2} style={{ gridTemplateColumns: '1fr' }}>
              <FieldItem label="Provider" value={medicalProvider} classes={classes} />
              <FieldItem label="Office Location" value={officeLocation?.name} classes={classes} />
            </div>
          </Col>
          <Col span={12}>
            <div className={classes.fieldGrid2} style={{ gridTemplateColumns: '1fr' }}>
              <FieldItem label="PCP Name" value={patient.pcpName} classes={classes} />
              <FieldItem label="PCP Phone" value={patient.pcpPhoneNumber} classes={classes} />
              <FieldItem label="PCP Fax" value={patient.pcpFaxNumber} classes={classes} />
            </div>
            {patient.alertRecipients?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <span className={classes.fieldLabel}>ALERT RECIPIENTS</span>
                <div className={classes.recipientList} style={{ marginTop: 4 }}>
                  {patient.alertRecipients.map((r, i) => (
                    <span key={r.email || i} className={classes.recipientItem}>
                      {r.firstName} {r.lastName}{' '}
                      <Text type="secondary" style={{ fontSize: 13 }}>({r.email})</Text>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </SectionCard>

      {/* ===== CARDIAC DEVICE ===== */}
      <SectionCard title="Cardiac Device" icon={<HeartOutlined />} collapsible defaultCollapsed={isSectionCollapsed('cardiacDevice')} onCollapseChange={handleCollapseChange('cardiacDevice')}>
        <div className={classes.deviceGrid}>
          <div>
            <div className={classes.fieldGrid2} style={{ gridTemplateColumns: '1fr' }}>
              <FieldItem label="Model" value={device?.modelName || 'N/A'} classes={classes} />
              <FieldItem label="Company" value={device?.modelManufacturer || 'N/A'} classes={classes} />
              <FieldItem label="Type" value={device?.modelType || 'N/A'} classes={classes} />
              <FieldItem label="Serial Number" value={device?.serialNumber || 'N/A'} classes={classes} />
              {patient.remoteMonitoring && (
                <>
                  <FieldItem label="Enrolled" value={patient.enrolled ? 'Yes' : 'No'} classes={classes} />
                  <FieldItem label="Paired" value={patient.paired ? 'Yes' : 'No'} classes={classes} />
                  <FieldItem label="Monitor Model Number" value={patient.homeMonitorSerialNumber} classes={classes} />
                </>
              )}
            </div>
            <div className={classes.longevityCard}>
              <EstimatedLongevity
                estimatedBatteryLongevity={patient?.currentDevice?.estimatedBatteryLongevity}
              />
            </div>
          </div>
          <div>
            <div className={classes.subTitle}>Lead Information</div>
            <LeadInfo currentLead={patient.currentLead} currentDevice={patient.currentDevice} />
          </div>
        </div>
      </SectionCard>

      {/* ===== DISEASES & HISTORY ===== */}
      <SectionCard title="Diseases & History" icon={<MedicineBoxOutlined />} collapsible defaultCollapsed={isSectionCollapsed('diseasesHistory')} onCollapseChange={handleCollapseChange('diseasesHistory')}>
        <DiseasesList patientId={id} />
        <div className={classes.statusRow}>
          <div className={classes.statusItem}>
            <CircleCheckIcon
              checked={patient?.alertForAnyAfib}
              style={{ color: patient?.alertForAnyAfib ? theme.brandSuccess : theme.brandDanger }}
            />
            <span>Alert for Any AFib</span>
            <Tooltip title="Send alerts whenever any atrial fibrillation event is detected for this patient.">
              <InfoCircleOutlined style={{ color: theme.textGrayColor, fontSize: 13 }} />
            </Tooltip>
          </div>
          <div className={classes.statusItem}>
            <CircleCheckIcon checked={patient?.oacContraindicated} />
            <span>OAC Contraindicated</span>
            <Tooltip title="Indicates oral anticoagulation is contraindicated for this patient.">
              <InfoCircleOutlined style={{ color: theme.textGrayColor, fontSize: 13 }} />
            </Tooltip>
          </div>
        </div>
        {icdCodes?.length > 0 && (
          <>
            <Divider className={classes.subDivider} />
            <div className={classes.subTitle}>ICD Codes</div>
            <div className={classes.icdList}>
              {icdCodes.map(code => (
                <div key={code.code} className={classes.icdItem}>
                  <span className={classes.icdCode}>{code.code}</span>
                  {code.description}
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>

      {/* ===== MEDICATIONS ===== */}
      {patient.patientMedications?.length > 0 && <SectionCard
        title="Medications"
        icon={<MedicineBoxOutlined />}
        collapsible
        defaultCollapsed={isSectionCollapsed('medications')}
        onCollapseChange={handleCollapseChange('medications')}
        extra={
          patient.medicationsLastSyncedAt && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              Synced {dayjs(patient.medicationsLastSyncedAt).format('MMM D, YYYY')}
            </Text>
          )
        }
      >
        <div className={classes.medList}>
            {patient.patientMedications.map(med => (
              <div key={med.id} className={classes.medItem}>
                <div className={classes.medHeader}>
                  <span className={classes.medName}>{med.name}</span>
                  <Tag
                    color={med.status === 'active' ? 'success' : 'default'}
                    style={{ borderRadius: 12, fontSize: 11 }}
                  >
                    {med.status}
                  </Tag>
                </div>
                <div className={classes.medDetails}>
                  {med.dosageInstructions && (
                    <span className={classes.medDetail}>{med.dosageInstructions}</span>
                  )}
                  {med.frequencyDisplay && (
                    <span className={classes.medDetail}>{med.frequencyDisplay}</span>
                  )}
                  {med.prescriberName && (
                    <span className={classes.medDetail}>Prescriber: {med.prescriberName}</span>
                  )}
                  {med.prescribedDate && (
                    <span className={classes.medDetail}>
                      Prescribed: {dayjs(med.prescribedDate).format('MMM D, YYYY')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
      </SectionCard>}

      {/* ===== NOTIFICATION PREFERENCES ===== */}
      <SectionCard
        title="Notification Preferences"
        icon={<BellOutlined />}
        collapsible
        defaultCollapsed={isSectionCollapsed('notificationPreferences')}
        onCollapseChange={handleCollapseChange('notificationPreferences')}
        extra={
          <Button onClick={showPreferencesModal} type="primary" size="small">
            Change
          </Button>
        }
      >
        <div className={classes.prefRow}>
          <div className={classes.prefItem}>
            <div
              className={classes.prefDot}
              style={{ backgroundColor: remindTransmissions ? theme.brandSuccess : '#d9d9d9' }}
            />
            <span className={classes.fieldLabel} style={{ textTransform: 'none', fontSize: 14 }}>
              Remind transmissions
            </span>
            <Tag color={remindTransmissions ? 'success' : 'default'} style={{ borderRadius: 12 }}>
              {remindTransmissions ? 'Yes' : 'No'}
            </Tag>
          </div>
          <div className={classes.prefItem}>
            <div
              className={classes.prefDot}
              style={{ backgroundColor: notifyDataReception ? theme.brandSuccess : '#d9d9d9' }}
            />
            <span className={classes.fieldLabel} style={{ textTransform: 'none', fontSize: 14 }}>
              Notify data reception
            </span>
            <Tag color={notifyDataReception ? 'success' : 'default'} style={{ borderRadius: 12 }}>
              {notifyDataReception ? 'Yes' : 'No'}
            </Tag>
          </div>
        </div>
        <Modal
          title="Preferences"
          open={preferencesModalVisible}
          onCancel={cancelPreferences}
          onOk={savePreferences}
          cancelText={null}
          okType="primary"
        >
          <List title="Notifications">
            <List.Item>
              <Checkbox checked={remindTransmissions} onChange={handleReminderChange}>
                Remind missed transmissions
              </Checkbox>
            </List.Item>
            <List.Item>
              <Checkbox checked={notifyDataReception} onChange={handleDataReceptionChange}>
                Notify data reception
              </Checkbox>
            </List.Item>
          </List>
        </Modal>
      </SectionCard>

      {/* ===== TIMELINE ===== */}
      <SectionCard title="Alerts, Remote Reports & Office Checks" icon={<CalendarOutlined />} collapsible defaultCollapsed={isSectionCollapsed('alertsRemoteReportsOfficeChecks')} onCollapseChange={handleCollapseChange('alertsRemoteReportsOfficeChecks')}>
        {id && practice?.id ? (
          <Timeline ref={timelineRef} patientId={id} practiceId={practice.id} />
        ) : (
          <Empty />
        )}
      </SectionCard>

      {/* ===== TRANSMISSIONS ===== */}
      <SectionCard
        title="Transmissions"
        icon={<SolutionOutlined />}
        collapsible
        defaultCollapsed={isSectionCollapsed('transmissions')}
        onCollapseChange={handleCollapseChange('transmissions')}
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            {transmissionsCount} total
          </Text>
        }
      >
        <div>
          {transmissionsList && transmissionsList.length > 0 ? (
            transmissionsList.map(item => (
              <div key={item.id} className={classes.trItem}>
                <div className={classes.trLeft}>
                  <div className={classes.trDot} />
                  <span className={classes.trDate}>
                    {item.transmissionDate && dayjs(item.transmissionDate).isValid()
                      ? dayjs(item.transmissionDate).format('MMM Do YYYY')
                      : 'Manually Uploaded'}
                  </span>
                  <Tag color="blue">{item.device.model}</Tag>
                </div>
                <Tooltip title="Open in a new tab" placement="top">
                  <Link to={`/transmissions/${item.id}`} target="_blank">
                    <Button type="text" size="small" icon={<ExportOutlined />} />
                  </Link>
                </Tooltip>
              </div>
            ))
          ) : (
            <Empty />
          )}
        </div>
        {(transmissionsList?.length || 0) < transmissionsCount && (
          <div className={classes.trFooter}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Showing latest {transmissionsList?.length || 0} of {transmissionsCount}
            </Text>
            <Button type="link" onClick={showTransmissions}>
              View all transmissions
            </Button>
          </div>
        )}
      </SectionCard>

      {/* ===== COMMENTS ===== */}
      <SectionCard title="Comments" icon={<MessageOutlined />} collapsible defaultCollapsed={isSectionCollapsed('comments')} onCollapseChange={handleCollapseChange('comments')}>
        <CommentsTables
          key={commentsVersion}
          collectionName="patients"
          id={patient.id}
          practiceId={patient.practiceId}
        />
      </SectionCard>

      {/* ===== MODALS ===== */}
      <Modal
        title="Removal options"
        open={removalOptionsVisible}
        onCancel={hideRemovalOptions}
        cancelText={null}
        okType="secondary"
        footer={null}
      >
        <List title="Deactivate">
          <List.Item>
            <div>
              <strong>Deactivate this patient</strong>
              <p>
                Won&apos;t be visible by default in the patient list but the
                information will be available.
              </p>
              <Select
                placeholder="Select reason for deactivation"
                value={deactivationSubType}
                onChange={setDeactivationSubType}
                style={{ width: '100%', marginTop: 8 }}
                options={DEACTIVATION_OPTIONS}
              />
              {SUBTYPES_WITH_FREE_TEXT.includes(deactivationSubType) && (
                <Input.TextArea
                  placeholder="Reason for deactivation"
                  value={deactivationReason}
                  onChange={e => setDeactivationReason(e.target.value)}
                  rows={3}
                  style={{ marginTop: 8 }}
                />
              )}
              <Button
                danger
                icon={<CloseCircleOutlined />}
                className={`delete-button ${classes.dangerZoneButton}`}
                onClick={handleDeactivate}
                loading={deactivating}
                disabled={!deactivationSubType}
                style={{ marginTop: 12 }}
              >
                Deactivate
              </Button>
            </div>
          </List.Item>
          <List.Item>
            <div>
              <strong>Full remove this patient</strong>
              <p>
                The patient will be deleted from the database. This action
                cannot be reverted.
              </p>
            </div>
            <Button
              danger
              icon={<DeleteOutlined />}
              className={`hard-delete-button ${classes.dangerZoneButton}`}
              onClick={showFullRemoval}
            >
              Full removal
            </Button>
          </List.Item>
        </List>
        <div className={classes.modalButtons}>
          <Divider />
          <Button type="secondary" onClick={hideRemovalOptions}>
            Close
          </Button>
        </div>
      </Modal>

      <Modal
        title="Patient reminders"
        open={remindersOptionsVisible}
        onCancel={hideRemindersOptions}
        cancelText={null}
        okType="secondary"
        footer={null}
      >
        <List title="Reminders">
          <List.Item>
            <div>
              <strong>Make a reminder phone call</strong>
              <p>A reminder call will be made to the patient&apos;s phone number.</p>
            </div>
            <Button
              type="primary"
              ghost
              icon={<PhoneOutlined />}
              className={`delete-button ${classes.dangerZoneButton}`}
              onClick={makeReminderCall}
              loading={makingReminderCall}
            >
              Make reminder call
            </Button>
          </List.Item>
          <List.Item>
            <div>
              <strong>Send an SMS reminder</strong>
              <p>An SMS message will be sent to the patient&apos;s mobile phone number.</p>
            </div>
            <Button
              type="primary"
              ghost
              icon={<MessageOutlined />}
              className={`hard-delete-button ${classes.dangerZoneButton}`}
              onClick={sendReminderSms}
              loading={sendingReminderSms}
            >
              Send reminder SMS
            </Button>
          </List.Item>
        </List>
        <div className={classes.modalButtons}>
          <Divider />
          <Button type="secondary" onClick={hideRemindersOptions}>
            Close
          </Button>
        </div>
      </Modal>

      <FullRemovalModal
        patientId={id}
        onCancel={hideFullRemoval}
        visible={fullRemovalVisible}
      />

      <TransmissionsModal
        patientId={patient.id}
        visible={showTransmissionsModal}
        onCancel={() => setShowTransmissionsModal(false)}
      />

      <ConnectivityNotificationModal
        patientId={id}
        visible={notificationModalVisible}
        onCancel={hideNotificationModal}
        onSuccess={handleNotificationSuccess}
      />
    </div>
  )
}
